/**
 * P-03 — pure rules of the engagement page and the per-type engagement form
 * (unit-tested in utils/__tests__/engagementPageModel.test.ts).
 */

/** D4 — a project / development engagement opens its project page; null = stay on the engagement page. */
export function engagementRedirectPath(engagement: {
  type: string;
  child_id?: string | null;
  project_id?: string | null;
  child?: { id?: string | null } | null;
}): string | null {
  const projectId = engagement.type === 'project'
    ? (engagement.child?.id ?? engagement.child_id ?? null)
    : engagement.type === 'development' ? (engagement.project_id ?? null) : null;
  return projectId ? `/projects/${projectId}` : null;
}

/**
 * 'YYYY-MM-DD' + 12 months (B2 default expiry). Calendar-correct: a day that
 * does not exist in the target month clamps to its last day
 * (2028-02-29 + 12 months = 2029-02-28).
 */
export function plusTwelveMonths(iso: string): string {
  return addMonths(iso, 12);
}

/** 'YYYY-MM-DD' + n months, clamping the day to the target month's last day; '' for invalid input. */
export function addMonths(iso: string, months: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return '';
  const total = Number(m[1]) * 12 + (Number(m[2]) - 1) + months;
  const year = Math.floor(total / 12);
  const month = total - year * 12; // 0-based
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const day = Math.min(Number(m[3]), lastDay);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${String(year).padStart(4, '0')}-${pad(month + 1)}-${pad(day)}`;
}

/** B2 — internal hourly rate = price ÷ hours (2 decimals), '' when not computable. */
export function internalRateOf(price: string, hours: string): string {
  const p = Number(price);
  const h = Number(hours);
  if (!price || !hours || !Number.isFinite(p) || !Number.isFinite(h) || !(h > 0)) return '';
  return String(Math.round((p / h) * 100) / 100);
}

/** D1 — utilization bar: red from 100%, orange from the warning threshold (default 80). */
export function utilizationTone(pct: number | null | undefined, warnPct = 80): 'danger' | 'warning' | 'primary' {
  const p = Number(pct ?? 0);
  if (p >= 100) return 'danger';
  if (p >= warnPct) return 'warning';
  return 'primary';
}

/** P-03/C — the server's 409 for a new task on an engagement without financial data. */
export const MISSING_FINANCIALS_CODE = 'ENGAGEMENT_MISSING_FINANCIALS';

/**
 * QA-R2/15 — the project to edit of a 409 ENGAGEMENT_MISSING_FINANCIALS on a
 * project / development (its price and payment terms are edited on the
 * project, not on the engagement page); null for any other error or type.
 */
export function missingFinancialsProjectId(err: unknown): string | null {
  type Body = { error_code?: string; details?: { engagement_type?: string; project_id?: string | null } };
  const e = err as { error?: Body; response?: { data?: { error?: Body } } } | null;
  const body = e?.error ?? e?.response?.data?.error;
  if (body?.error_code !== MISSING_FINANCIALS_CODE) return null;
  const kind = body.details?.engagement_type;
  return kind === 'project' || kind === 'development' ? (body.details?.project_id ?? null) : null;
}

/** The engagement id of a 409 ENGAGEMENT_MISSING_FINANCIALS error (null for any other error). */
export function missingFinancialsEngagementId(err: unknown): string | null {
  type Body = { error_code?: string; details?: { engagement_id?: string } };
  const e = err as { error?: Body; response?: { data?: { error?: Body } } } | null;
  const body = e?.error ?? e?.response?.data?.error;
  return body?.error_code === MISSING_FINANCIALS_CODE ? (body.details?.engagement_id ?? null) : null;
}

/**
 * D1 — the service contract's "this month" usage line and bar, computed on ONE
 * base: quota X + approved extra N. Text "H:MM מתוך X ש' (+N ש' מאושרות) · P%"
 * where P = used ÷ (X + N); the bar's tone uses the same P.
 */
export function serviceContractUsage(
  quotaHours: number | null | undefined,
  utilization: { consumed_hours?: number | string | null; extra_hours?: number | string | null } | null | undefined,
): { usedMinutes: number; quota: number; extra: number; total: number; pct: number } | null {
  const quota = quotaHours != null && Number.isFinite(Number(quotaHours)) ? Number(quotaHours) : null;
  if (quota == null) return null;
  const used = Number(utilization?.consumed_hours ?? 0) || 0;
  const extraRaw = Number(utilization?.extra_hours ?? 0) || 0;
  const extra = extraRaw > 0 ? extraRaw : 0;
  const total = quota + extra;
  const pct = total > 0 ? Math.round((used / total) * 1000) / 10 : 0;
  return { usedMinutes: Math.round(used * 60), quota, extra, total, pct };
}

/**
 * D-289 (BQ-074) - "שעות מחוץ לתקופת החוזה": the hours logged in months the service contract is not in force for
 * (server: `out_of_period`, sent to the OTE manager / the account manager only). null = nothing to show.
 * Months come back as "MM/YYYY", in order.
 */
export function outOfPeriodWarning(
  summary: { hours?: number | string | null; logs?: number | string | null; months?: Array<{ month: string }> | null } | null | undefined,
): { hours: number; logs: number; months: string[] } | null {
  const hours = Number(summary?.hours ?? 0) || 0;
  const logs = Number(summary?.logs ?? 0) || 0;
  if (!(hours > 0) && !(logs > 0)) return null;
  const months = (summary?.months ?? [])
    .map((m) => m?.month)
    .filter((m): m is string => typeof m === 'string' && /^\d{4}-\d{2}$/.test(m))
    .sort()
    .map((m) => `${m.slice(5, 7)}/${m.slice(0, 4)}`);
  return { hours, logs, months };
}
